import {
  Component,
  DoCheck,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AutomationHelpers } from 'app/automations/automation-helpers';
import {
  ACTIONS_TYPES,
  ACTIONS_TYPES_REAL_TIME,
  ActionListField,
  ActionUpdateField,
  ActionUpdateTags,
  ActionsPropertiesDetails,
  AppWithFieldWithoutList,
  PROPS_TYPE,
  TriggerbeeTagActions,
} from 'app/automations/automation-helpers-actions';
import {
  CustomModelAction,
  CustomModelProperty,
  TriggerCondition,
  TypeInput,
  TypeStep,
} from 'app/automations/automations-models';
import { DataForApsisOnePropType, list } from 'app/automations/edit-automation/edit-action/dynamicTagsList';
import { AutomationExtensions } from 'app/campaign/widget-automations.model';
import WidgetComponentHelpers from 'app/campaign/widget-component-helpers';
import { Widget, WidgetComponentSubType, WidgetSimpleInfo, WidgetState } from 'app/campaign/widgets.model';
import { CurrentSiteIdStore } from 'app/core/current-site-id.store';
import { LanguageService } from 'app/core/language.service';
import { IntegrationsService } from 'app/shared/services/integrations.service';
import { SelectionsService } from 'app/shared/services/selections.service';
import { SnackBarService } from '../../core/snack-bar.service';
import { CurrentContext, CurrentContextService } from '../../core/current-context.service';
import { EmailTemplateService } from '../../email-templates/email-template.service';
import { FlyoutOpeningData } from '../flyout-models';
import { FlyoutService } from '../flyout.service';
import { DefaultFieldMappingsService } from './default-field-mappings.service';
import { WidgetAutomationsDataService } from 'app/campaign/widget-automations-data.service';
import { UserSession } from '../../core/user-session';
import { Account, CustomProfileProperty } from 'app/back-office/account.model';
import WidgetAutomationHelpers from 'app/campaign/widget-automation-helpers';
import { IntegrationType } from 'app/automations/integrations-models';

@Component({
  selector: 'app-action-automation',
  templateUrl: './action-automation.component.html',
  styleUrl: './action-automation.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ActionAutomationComponent implements OnInit, DoCheck, OnDestroy {
  @Output() setPropertyList = new EventEmitter();
  @Output() dynamicModifyingMapFieldInRealTime = new EventEmitter();
  public action: CustomModelAction;
  public properties: CustomModelProperty[] | any;
  public triggers: TriggerCondition[];
  public actionPropertiesDetails = ActionsPropertiesDetails;
  public TypeInput = TypeInput;
  public isLoading = false;
  public ACTIONS_TYPES = ACTIONS_TYPES;
  public TypeStep = TypeStep;
  public PROPS_TYPE = PROPS_TYPE;
  public ActionUpdateField: string[] = ActionUpdateField;
  public ActionUpdateTags: string[] = ActionUpdateTags;
  public validPairField = {};
  public typePage: string;
  public listUIisExist = false;
  public lockDropDown = false;
  public propValueTags: Array<any> = [];
  public widgets: WidgetSimpleInfo[] = [];
  public listUIforMap = [];
  private completedRequest = [];
  public isListAlwaysAdded: boolean | undefined;
  public isAdmin: boolean = this.userSession.context.isAdmin;
  public dataStateAndAccount: { widgetState: WidgetState[]; account: Account; actionsConfig: any };
  public missingTagsOrLists: string[] = [];
  // Working rows for FieldMappingDictionary properties. Keyed by property name.
  // Edits in the UI mutate these rows; compileFieldMappingRows pushes the result back to prop.value as a Dictionary.
  public fieldMappingRows: { [propName: string]: Array<{ sourceField: string; destinationPath: string }> } = {};
  // Triggerbee fields exposed in the source picker for FieldMappingDictionary rows
  // (same options as the "Enter text or a field in Triggerbee" picker for update-field actions),
  // plus any custom tags the user previously added via [addTag]="true" so they're re-rendered with
  // their stored value as the label on flyout re-open.
  public triggerbeeFieldsList: Array<{ value: string; label: string; group: string }> = [...list];
  private static readonly CUSTOM_FIELD_GROUP = 'automations.triggers.groups.contact';
  private static readonly CUSTOM_PROFILE_FIELD_GROUP = 'automations.triggers.groups.customProfileFields';
  private list: Array<{ value: string; label: string; group: string }> = structuredClone(list);
  private customValueOptions: Array<{ value: string; label: string; group: string }> = [];
  private outcomeFieldOptions: Array<{ value: string; label: string; group: string }> = [];
  private customProfileFieldOptions: Array<{ value: string; label: string; group: string }> = [];
  private customProfileFieldKeys = new Set<string>();
  public ACTIONS_TYPES_REAL_TIME = Object.values(ACTIONS_TYPES_REAL_TIME);
  public isfieldMappingsPropOnAction: boolean;
  private openingWidget: Widget | undefined;

  @Input() set widgetStates(data) {
    if (!data.widgetState || !data.account || !data.actionsConfig) return;
    this.dataStateAndAccount = data;
    this.isListAlwaysAdded = this.checkRunningInRealTime(data.widgetState, data.account);
  }
  @Input() nameGroup: string;
  /** Opt-in: lock the switch only on mappings already persisted as update actions. Off by default because
   *  only hosts keeping WidgetAutomationsDataService.existingAutomation in sync can answer that. */
  @Input() public realTimeLockFromPersistedMappingOnly = false;
  public realTimeSwitchLocked = false;
  // Opt-in (default off, so existing hosts are unchanged): render the ng-select dropdowns in a body overlay
  // instead of inline, so they aren't clipped by an ancestor with overflow:hidden (e.g. an accordion panel).
  @Input() appendDropdownToBody = false;
  @Input() listsIntegrations: any;
  @Input() public set fieldsInOutcomes(fields: Array<{ value: string; label: string; group: string }>) {
    this.outcomeFieldOptions = fields ?? [];
    this.rebuildFieldsList();
  }
  @Input() public set customProfileFields(fields: CustomProfileProperty[]) {
    this.customProfileFieldOptions = (fields ?? []).map((field) => ({
      label: field.name,
      // MergeTagBuilder only substitutes {{...}} tokens, so a bare mapTo reaches the CRM verbatim.
      value: `{{contact.custom.${field.mapTo}}}`,
      group: ActionAutomationComponent.CUSTOM_PROFILE_FIELD_GROUP,
    }));
    this.customProfileFieldKeys = new Set((fields ?? []).map((field) => field.mapTo));
    this.rebuildFieldsList();
  }
  @Input() public set proceedRequiredListActions(actionsGroup: CustomModelAction) {
    if (actionsGroup) {
      const fieldNames = this.properties.filter((prop) => prop.name === PROPS_TYPE.FieldName);
      if (fieldNames.length) {
        this.handleRequest(
          this.integrationsService.getDataForActionProperties(`${fieldNames[0].url}?listId=${actionsGroup}`),
          fieldNames,
        );
      }
    }
  }
  @Input() public set flyoutOpeningData(openingData: FlyoutOpeningData) {
    this.typePage = openingData.type;
    this.widgets = openingData.widgets;
    this.triggers = openingData.triggers;
    this.triggers = openingData.triggers;
    this.openingWidget = openingData.widget;
    this.addCustomProfileFields(openingData?.customProfileFields);
    this.action = this.modifyActions(openingData?.action);
    this.properties = this.action.properties;
    // Recompute the shared field-mapping flag once the default mapping has actually been applied
    // (it lands asynchronously), so the "Run in realtime" switch reflects the auto-filled mapping.
    this.defaultFieldMappingsService
      .setDefaultFieldMapping(this.action, this.properties)
      .subscribe(() => this.recomputeFieldMappingFlag());
    this.listsIntegrations = openingData.listsIntegrations || this.listsIntegrations;
    this.getData(this.action);
    this.isfieldMappingsPropOnAction = this.hasFieldMappingsProp();
    this.updateSwitchRealTimeTooltip();
  }

  private currentContext: CurrentContext;
  private cleverReachEnforceDoubleOptIn = false;
  private cleverReachIntegrationGroup: string | undefined;

  constructor(
    protected integrationsService: IntegrationsService,
    public flyoutService: FlyoutService,
    public currentSiteIdStore: CurrentSiteIdStore,
    public selectionsService: SelectionsService,
    public snackBarService: SnackBarService,
    public translateService: TranslateService,
    public languageService: LanguageService,
    private defaultFieldMappingsService: DefaultFieldMappingsService,
    private widgetAutomationsDataService: WidgetAutomationsDataService,
    private readonly userSession: UserSession,
    private readonly currentContextService: CurrentContextService,
    private readonly emailTemplateService: EmailTemplateService,
  ) {
    this.currentContextService.currentContextBS.subscribe((currentContext) => {
      if (currentContext) {
        this.currentContext = currentContext;
      }
    });
    this.integrationsService.activeIntegrationsBS.subscribe((integrations) => {
      if (integrations) {
        const cleverReach = integrations.find((i) => i.type === IntegrationType.CleverReach);
        this.cleverReachEnforceDoubleOptIn = cleverReach?.data?.enforceDoubleOptIn === true;
        this.cleverReachIntegrationGroup = cleverReach?.data?.group;
      }
    });
  }

  getData(action: CustomModelAction) {
    for (let index = 0; index < this.properties.length; index++) {
      let request$;
      const property = this.properties[index];
      const propsTriggerbee = this.properties.filter((prop) => prop.translationKey === 'property-in-Triggerbee');
      if (propsTriggerbee.length) {
        propsTriggerbee.forEach((prop) => (prop.listUI = this.list));
        this.setValueAction(action, property, index);
      }
      const propsEquelUrl = this.properties.filter((prop) => prop.url && prop.url === property.url);
      if (
        propsEquelUrl.length &&
        propsEquelUrl.some((prop) => prop.listUI) &&
        !ActionUpdateTags.includes(this.action.$type as ACTIONS_TYPES)
      ) {
        const propListUI = propsEquelUrl.find((prop) => prop.listUI).listUI;
        propsEquelUrl.forEach((prop) => (prop.listUI = propListUI));
        continue;
      }
      if (action.$type === ACTIONS_TYPES.TriggerbeeSendMail) {
        const siteId = this.currentSiteIdStore.get() ?? 0;
        request$ = this.selectionsService.getPersonsAsync(siteId);
        this.handleRequest(request$, property);
      } else if (action.$type === ACTIONS_TYPES.TriggerbeeTransactionEmail) {
        const customerId = this.currentContext.customer.id;
        if (customerId) {
          request$ = this.emailTemplateService.getByCustomer(customerId);
          this.handleRequest(request$, property);
        }
      } else if (action.nameAction === AutomationExtensions.Triggerbee && property.url) {
        request$ = this.integrationsService.getDataForActionPropertyTrigerbee(property.url);
        this.handleRequest(request$, property);
      } else if (action.$type === ACTIONS_TYPES.MailchimpUpdateMergeTag && property.name === PROPS_TYPE.FieldName) {
        if (action.listId) {
          const fieldNames = this.properties.filter((prop) => prop.name === PROPS_TYPE.FieldName);
          if (!this.listUIisExist) {
            this.listUIisExist = true;
            if (this.completedRequest.every((item) => item.url !== property.url)) {
              this.handleRequest(
                this.integrationsService.getDataForActionProperties(
                  `${fieldNames[0].url}?listId=${this.action.listId}`,
                ),
                fieldNames,
              );
              this.completedRequest.push(property);
            }
          }
          continue;
        }
      } else if (action.$type === ACTIONS_TYPES.ApsisOneAddToSubscription && property.name === PROPS_TYPE.Type) {
        property.listUI = DataForApsisOnePropType;
      } else if (property.url) {
        request$ = this.integrationsService.getDataForActionProperties(property.url);
        if (this.completedRequest.every((item) => item.url !== property.url)) {
          let stackProperties = [];
          if (this.action.$type === ACTIONS_TYPES.VoyadoUpdateConsentPreferences) {
            stackProperties = this.properties.filter((prop) => prop.url === property.url);
          } else {
            stackProperties = this.properties.filter((prop) => prop.name === property.name);
          }
          this.handleRequest(request$, stackProperties);
          this.completedRequest.push(property);
        }
      }
      this.setValueAction(action, property, index);
    }
    this.injectCleverReachDoiFormProperty(action);
  }

  private injectCleverReachDoiFormProperty(action: CustomModelAction): void {
    if (action.nameAction !== 'CleverReach' || !this.cleverReachEnforceDoubleOptIn) return;
    if (action.$type !== ACTIONS_TYPES.CleverReachCreateContact) return;
    const hasDoi = this.properties.find((p) => p.name === 'doubleOptInFormId');
    if (hasDoi) return;
    const doiProp = {
      name: 'doubleOptInFormId',
      typeInput: TypeInput.List,
      url: '',
      translationKey: 'doi-form',
      description: '',
      required: true,
      addEmptyOption: true,
      listUI: [],
      value: action['doubleOptInFormId'] || '',
    };
    this.properties.push(doiProp);
    if (this.cleverReachIntegrationGroup) {
      this.loadCleverReachDoiForms(this.cleverReachIntegrationGroup);
    }
  }

  private loadCleverReachDoiForms(groupId: string): void {
    const doiProp = this.properties.find((p) => p.name === 'doubleOptInFormId');
    if (!doiProp) return;
    doiProp.listUI = [];
    this.integrationsService.getDataForActionProperties(`/CleverReach/doi-forms/${groupId}`).subscribe(
      (result) => {
        doiProp.listUI = result.data || result;
      },
      () => {
        this.snackBarService.error('generic.error');
      },
    );
  }

  public showsTagProfileSwitch(): boolean {
    return TriggerbeeTagActions.includes(this.action?.$type as ACTIONS_TYPES);
  }

  public saveAction(): void {
    // Defensive: re-compile every FieldMappingDictionary property from its working rows so the
    // emitted properties array always reflects the current UI state, even if a (ngModelChange)
    // event was missed for some reason.
    this.properties?.forEach((prop) => {
      if (prop?.typeInput === TypeInput.FieldMappingDictionary) {
        this.writeFieldMappingsToProperty(prop);
      }
    });
    this.flyoutService.flyoutActionAddBS.next(this.properties);
    this.flyoutService.close();
  }

  public handleRequest(request, property): void {
    this.isLoading = true;
    if (this.lockDropDown) {
      this.isLoading = false;
    }
    if (request instanceof Promise) {
      request.then(
        (result) => {
          property.listUI = result.data || result;
          this.checkOnMissingTagsOrLists(property);
          this.isLoading = false;
        },
        () => {
          this.isLoading = false;
          this.snackBarService.error('generic.error');
        },
      );
    } else {
      request.subscribe(
        (result) => {
          if (Array.isArray(property)) {
            property.forEach((prop) => {
              prop.listUI = result.data || result;
              this.selectListIdsFromIntegration(prop);
            });
          } else {
            property.listUI = result.data || result;
            this.prettifyGoalsNames(property);
          }
          this.checkOnMissingTagsOrLists(property);
          this.isLoading = false;
        },
        () => {
          this.listUIisExist = false;
          this.isLoading = false;
          this.snackBarService.error('generic.error');
        },
      );
    }
  }

  private setValueAction(action, property, index?): void {
    if (ActionUpdateField.includes(action.$type)) {
      this.addCustomItemInList(property);
      if (this.typePage === 'Automation') {
        const pairActions = [];
        for (const key in action) {
          if (typeof action[key] === 'object' && !Array.isArray(action[key])) {
            if (
              AppWithFieldWithoutList.includes(action.nameAction) ||
              this.ACTIONS_TYPES_REAL_TIME.includes(action.$type as ACTIONS_TYPES_REAL_TIME) ||
              action.properties.every((prop) => prop.name !== PROPS_TYPE.TagIds && prop.name !== PROPS_TYPE.ListId)
            ) {
              pairActions.push(action[key], action[key]);
            } else {
              pairActions.push(action[key], action[key], action[key]);
            }
          }
        }
        if (
          !pairActions.length &&
          action.requiredList &&
          (property.name === PROPS_TYPE.ListId || property.name === PROPS_TYPE.TagIds)
        ) {
          property.value = action[property.name];
        }
        pairActions.forEach((action, i) => {
          if (i === index) {
            property.value =
              typeof [property.name] === 'string'
                ? action[property.name]
                : property.name === PROPS_TYPE.TagIds
                  ? action[property.name]
                  : action[property.name].toString();
            this.addCustomItemInList(property);
          }
        });
      }
      action.properties
        .filter((act) => act.name !== PROPS_TYPE.ListId && act.name !== PROPS_TYPE.TagIds)
        .forEach((prop, index) => {
          if (prop.value) this.validPairField[index] = true;
        });
      this.setMapNameOnFieldValue(property);
    } else if (ActionUpdateTags.includes(this.action.$type as ACTIONS_TYPES) && property.name === PROPS_TYPE.Tags) {
      if (!action[property.name]) action[property.name] = [''];
      this.propValueTags = action[property.name].map((item) => ({ value: item }));
      this.setTagValue(property);
    } else {
      if (property.defaultValue !== undefined && action[property.name] === '') {
        property.value = property.defaultValue;
      } else if (property.typeInput === TypeInput.Switch && action[property.name] === '') {
        property.value = false;
      } else if (action.$type === ACTIONS_TYPES.VoyadoUpdateConsentPreferences) {
        if (action[property.name] === '' || action[property.name] === null) {
          property.value = 'none';
        } else if (action[property.name] || action[property.name] === false) {
          property.value = String(action[property.name]);
        }
      } else if (property.typeInput === TypeInput.FieldMappingDictionary) {
        this.initFieldMappingRows(property, action[property.name]);
      } else if (typeof property.name === 'string') {
        if (property.name === 'cartGoalArgument' && this.triggers[0].type === 'Goals') {
          property.value = this.triggers[0].argument;
          //set hide for backwards compatibility
          property.hide = true;
        } else {
          property.value = action[property.name];
        }
      }
    }
  }

  // Hydrate fieldMappingRows from the saved Dictionary<string,string> (or default to a single empty row).
  private initFieldMappingRows(property: CustomModelProperty, saved: unknown): void {
    const savedDict =
      saved && typeof saved === 'object' && !Array.isArray(saved) ? (saved as Record<string, string>) : {};
    const rows = Object.entries(savedDict).map(([sourceField, destinationPath]) => ({
      sourceField,
      destinationPath: destinationPath ?? '',
    }));
    this.fieldMappingRows[property.name] = rows.length > 0 ? rows : [{ sourceField: '', destinationPath: '' }];
    // Re-register any previously-saved custom tags so ng-select can render their labels when the
    // flyout reopens — otherwise values not in the built-in list show up blank.
    for (const row of this.fieldMappingRows[property.name]) {
      this.registerCustomTagItemIfMissing(row.sourceField);
    }
    property.value = this.compileRowsToDict(property.name);
  }

  private registerCustomTagItemIfMissing(value: string): void {
    if (!value) return;
    if (this.triggerbeeFieldsList.some((item) => item.value === value)) return;
    this.triggerbeeFieldsList = [
      ...this.triggerbeeFieldsList,
      { value, label: value, group: ActionAutomationComponent.CUSTOM_FIELD_GROUP },
    ];
  }

  // Convert the working rows back into a Dictionary<string,string> and push to prop.value.
  // Skips rows where either source or destination is blank.
  private compileRowsToDict(propName: string): Record<string, string> {
    const compiled: Record<string, string> = {};
    const rows = this.fieldMappingRows[propName] ?? [];
    for (const row of rows) {
      const source = (row.sourceField ?? '').trim();
      const destination = (row.destinationPath ?? '').trim();
      if (source && destination) {
        compiled[source] = destination;
      }
    }
    return compiled;
  }

  public onFieldMappingRowChange(property: CustomModelProperty): void {
    this.writeFieldMappingsToProperty(property);
  }

  // Explicit setters that mutate fieldMappingRows[name][index] by index — avoids any quirk
  // where template event expressions of the form `row.x = $event` don't take effect inside
  // the new @for control-flow loops.
  public setFieldMappingSource(property: CustomModelProperty, rowIndex: number, value: string): void {
    const row = this.fieldMappingRows[property.name]?.[rowIndex];
    if (!row) return;
    // ng-select fires (ngModelChange) with null on blur/dropdown-close even when [clearable]=false.
    // Any null/undefined is spurious — only accept real string values.
    if (value === null || value === undefined) return;
    row.sourceField = value;
    this.onFieldMappingRowChange(property);
  }

  // ng-select's own (change) event — fires ONLY when user actually picks from the dropdown or
  // adds a tag. Unlike (ngModelChange), it doesn't fire on internal state resets to null. So this
  // is the most reliable signal that a real user pick happened. $event is the picked item object
  // (e.g. { value: '{{contact.email}}', label: 'Email', group: '...' }) — we extract item.value.
  public onFieldMappingSourcePicked(property: CustomModelProperty, rowIndex: number, item: any): void {
    const row = this.fieldMappingRows[property.name]?.[rowIndex];
    if (!row || !item) return;
    const pickedValue = typeof item === 'string' ? item : (item.value ?? item.label ?? '');
    if (!pickedValue) return;
    row.sourceField = pickedValue;
    this.registerCustomTagItemIfMissing(pickedValue);
    this.onFieldMappingRowChange(property);
  }

  public setFieldMappingDestination(property: CustomModelProperty, rowIndex: number, value: string): void {
    const row = this.fieldMappingRows[property.name]?.[rowIndex];
    if (!row) return;
    row.destinationPath = value;
    this.onFieldMappingRowChange(property);
  }

  public addFieldMappingRow(property: CustomModelProperty): void {
    if (!this.fieldMappingRows[property.name]) {
      this.fieldMappingRows[property.name] = [];
    }
    this.fieldMappingRows[property.name].push({ sourceField: '', destinationPath: '' });
    this.writeFieldMappingsToProperty(property);
  }

  public removeFieldMappingRow(property: CustomModelProperty, index: number): void {
    const rows = this.fieldMappingRows[property.name];
    if (!rows) return;
    rows.splice(index, 1);
    if (rows.length === 0) {
      rows.push({ sourceField: '', destinationPath: '' });
    }
    this.writeFieldMappingsToProperty(property);
  }

  // Push the compiled dict onto BOTH prop.value (read by composeAction) and the action object
  // itself (defensive — composeAction shouldn't be bypassed, but a direct write means the
  // action's own field is always in sync with what's on screen).
  private writeFieldMappingsToProperty(property: CustomModelProperty): void {
    const compiled = this.compileRowsToDict(property.name);
    property.value = compiled;
    if (this.action) {
      this.action[property.name] = compiled;
    }
  }

  private modifyActions(action): CustomModelAction {
    if (action.length && action.every((item) => ActionUpdateField.find((act) => act === item.$type))) {
      const combinedProperties = JSON.parse(JSON.stringify(action)).reduce((acc, obj) => {
        return acc.concat(obj.properties);
      }, []);
      const combinedAction = [].concat(...action);
      const combinedActionProp = Object.assign({}, ...action);
      return { ...combinedAction, ...combinedActionProp, properties: combinedProperties };
    } else {
      return action;
    }
  }

  public addOtherFields(): void {
    const fieldName = JSON.parse(JSON.stringify(this.properties.find((prop) => prop.name === PROPS_TYPE.FieldName)));
    delete fieldName.value;
    const fieldValue = JSON.parse(JSON.stringify(this.properties.find((prop) => prop.name === PROPS_TYPE.FieldValue)));
    delete fieldValue.value;
    delete fieldValue.mapName;
    this.properties.push(fieldValue, fieldName);
    this.action.properties = [...this.properties];
  }

  public removeField(index): void {
    //if amount of fields === 2 we should just empty value
    if (
      this.properties.length === 2 ||
      (this.properties.length === 3 &&
        (this.properties[0].name === PROPS_TYPE.ListId || this.properties[0].name === PROPS_TYPE.TagIds))
    ) {
      this.properties.forEach((prop) => {
        if (prop.name !== PROPS_TYPE.ListId && prop.name !== PROPS_TYPE.TagIds) prop.value = '';
      });
      this.action.properties = [...this.properties];
      this.validPairField[0] = false;
    } else {
      //if amount of fields > 2 left we need to delete fields completely
      if (this.properties[0].name === PROPS_TYPE.FieldValue) {
        // The index is always one more than we want. But if we have ex a listId-property first it will be correct
        index = index - 1;
      }
      if (
        this.typePage === 'Automation' &&
        !AppWithFieldWithoutList.includes(this.action.nameAction) &&
        !this.ACTIONS_TYPES_REAL_TIME.includes(this.action.$type as ACTIONS_TYPES_REAL_TIME)
      ) {
        if (this.propList?.length && this.propList[0]) this.properties = [...this.propList, ...this.propsField];
        else this.properties = [...this.propsField];
      }
      this.properties.splice(index, 2);
      this.action.properties = [...this.properties];
    }
    delete this.validPairField[index];
    delete this.validPairField[index - 1];
    const recalculatedValidPairField = Object.values(this.validPairField).reduce((acc, value, index) => {
      acc[index] = value;
      return acc;
    }, {});
    this.validPairField = recalculatedValidPairField;
  }

  public onDropdownSelectChange(prop, event, index) {
    if (
      ActionUpdateField.includes(this.action.$type as ACTIONS_TYPES) &&
      prop.name === PROPS_TYPE.FieldValue &&
      prop.listUI.every((item) => item.label !== event.label)
    ) {
      this.properties.forEach((prop) => {
        if (prop.name === PROPS_TYPE.FieldValue) {
          prop.listUI = [
            ...prop.listUI,
            {
              label: event.label,
              value: event.label,
              group: 'Custom',
            },
          ];
        }
      });

      if (this.typePage !== 'Automation' || AppWithFieldWithoutList.includes(this.action.nameAction)) {
        const propList = AutomationHelpers.pickPropListId(this.properties);
        let idx = index;
        if (propList) idx = index + 1;
        this.properties[idx] = { ...this.properties[idx], value: event.label };
        this.properties = [...this.properties];
        this.action.properties = [...this.properties];
        this.validPairField[index] = true;
      } else {
        let count = 0;
        this.properties.forEach((prop) => {
          if (prop.name === PROPS_TYPE.FieldValue || prop.name === PROPS_TYPE.FieldName) {
            prop.idx = count;
            count++;
          }
        });
        const idx = this.properties.findIndex((y) => y.idx === index);
        this.properties[idx] = { ...this.properties[idx], value: event.label };
        this.properties = [...this.properties];
        this.validPairField[index] = true;
        this.properties.forEach((prop) => delete prop.idx);
      }
    } else {
      this.defaultFieldMappingsService.saveDefaultFieldMapping(prop, index, this.properties, this.action.nameAction);
      this.validPairField[index] = true;
    }
    // Mapping a/unmapping a destination field must immediately re-evaluate the "Run in realtime"
    // switch on the sibling steps — otherwise it stays disabled until a full save + reload.
    if (this.isFieldMapping) {
      this.recomputeFieldMappingFlag();
    }
  }

  public setListIdOnFieldProperty(property, index) {
    if (this.action.$type === ACTIONS_TYPES.RuleMailerRemoveTagFromSubscriber) return;
    this.validPairField[index] = true;
    if (
      ActionListField.includes(this.action.$type as ACTIONS_TYPES) &&
      (property.name === PROPS_TYPE.ListId || property.name === PROPS_TYPE.TagIds) &&
      property.value
    ) {
      this.setPropertyList.emit({ action: this.action, property: property });
    }
    const propList = AutomationHelpers.pickPropListId(this.properties);
    if (propList?.value) {
      const propListIndex = this.properties.findIndex(
        (p) => p.name === PROPS_TYPE.ListId || p.name === PROPS_TYPE.TagIds,
      );
      const propsBefore = this.properties.slice(0, propListIndex);
      let propsRequiredListId = AutomationHelpers.excludePropListId(this.properties);
      if (propsBefore?.length) {
        const propsBeforeNames = new Set(propsBefore.map((p) => p.name));
        propsRequiredListId = propsRequiredListId.filter((p) => !propsBeforeNames.has(p.name));
      }
      propsRequiredListId.forEach((prop) => (prop.listId = propList.value));
      this.properties = [...propsBefore, propList, ...propsRequiredListId];
      const propListName = this.action.nameAction === 'Rule' ? PROPS_TYPE.TagIds : PROPS_TYPE.ListId;
      this.action[propListName] = propList.value;
      if (this.action.$type === ACTIONS_TYPES.MailchimpUpdateMergeTag) {
        const fieldNames = this.properties.filter((prop) => prop.name === PROPS_TYPE.FieldName);
        if (fieldNames[0].listId) {
          this.handleRequest(
            this.integrationsService.getDataForActionProperties(`${fieldNames[0].url}?listId=${this.action.listId}`),
            fieldNames,
          );
        }
      }
      this.setPropertyList.emit({ action: this.action, property: propList[0] || propList });
    }
  }

  public addTagFields(): void {
    this.propValueTags.push({ value: '' });
  }

  public setTagValue(prop): void {
    prop.value = this.propValueTags.map((item) => item.value);
    if (!(this.propValueTags.length === 1 && !this.propValueTags[0].value)) {
      prop.value = prop.value.filter((item) => item);
    }
  }

  public deleteTagFields(index, prop) {
    if (this.propValueTags.length === 1) {
      this.propValueTags[0].value = '';
      prop.value = [''];
    } else {
      this.propValueTags = this.propValueTags.filter((item, i) => index !== i);
      prop.value = this.propValueTags.map((item) => item.value);
    }
  }

  private addCustomItemInList(prop) {
    if (!prop.value || this.isMapFieldProp(prop)) return;
    if (prop.name === PROPS_TYPE.FieldValue) {
      const field_exist = this.list.find((item) => item.value === prop.value);
      if (!field_exist) {
        const customOption = {
          label: prop.value,
          value: prop.value,
          group: 'Custom',
        };
        this.customValueOptions.push(customOption);
        prop.listUI.push(customOption);
      }
    }
  }

  private addCustomProfileFields(fields: CustomProfileProperty[]) {
    if (!fields?.length) {
      return;
    }
    this.customProfileFields = fields;
  }

  // getData() aliases this.list into prop.listUI, so mutate in place instead of reassigning —
  // options arriving after that point would otherwise never reach the rendered dropdown.
  private rebuildFieldsList(): void {
    const options = [
      ...structuredClone(list),
      // An outcome writing to a known profile field is already offered under Custom Profile Fields.
      ...this.outcomeFieldOptions.filter((option) => !this.customProfileFieldKeys.has(this.mergeTagKey(option.value))),
      ...this.customProfileFieldOptions,
    ];
    const offered = new Set(options.map((option) => option.value));
    this.list.length = 0;
    // The input setters calling this run after getData(), which already added any manually typed value.
    this.list.push(...options, ...this.customValueOptions.filter((option) => !offered.has(option.value)));
  }

  /** Strips a {{session.custom.x}} / {{contact.custom.x}} tag down to its custom-property key. */
  private mergeTagKey(value: string): string {
    return (value ?? '').replace(/^{{(?:session|contact)\.custom\./, '').replace(/}}$/, '');
  }

  private prettifyGoalsNames(property) {
    if (this.action.$type === ACTIONS_TYPES.TriggerbeeCartAbandonment) {
      property.listUI = property.listUI.map((item) => ({ ...item, displayName: item.name }));
      property.listUI = AutomationHelpers.prettifyGoalsNames(
        this.translateService,
        this.widgets,
        property.listUI,
        'displayName',
      );
    }
  }

  onMouseEnter(): void {
    if (this.action.nameAction === 'Zapier' && !this.lockDropDown) {
      this.lockDropDown = true;
      const action = this.action;
      for (let index = 0; index < this.properties.length; index++) {
        let request$;
        const property = this.properties[index];
        if (property.url) {
          request$ = this.integrationsService.getDataForActionProperties(property.url);

          let stackProperties = [];
          stackProperties = this.properties.filter((prop) => prop.name === property.name);
          this.handleRequest(request$, stackProperties[0]);
          this.completedRequest.push(property);
        }
      }
    }
  }

  onMouseLeave(): void {
    if (this.action.nameAction === 'Zapier') {
      this.lockDropDown = false;
    }
  }

  private setMapNameOnFieldValue(prop) {
    if (this.typePage === 'Automation' && this.isMapFieldProp(prop)) {
      prop.mapName = prop.value;
      this.properties.forEach((prop) => {
        if (this.isMapFieldProp(prop)) {
          this.listUIforMap.push({
            label: prop.value,
            value: prop.value,
            group: 'Custom',
          });
        }
      });
    }
  }

  private isMapFieldProp(prop: any) {
    if (!this.inputFieldNames.size) return false;
    return (
      !Array.isArray(prop.value) &&
      prop.value?.startsWith('{{session.custom.') &&
      this.inputFieldNames.has(prop.value) &&
      prop.value.endsWith('}}')
    );
  }

  get isBtnAddFieldDisabled() {
    if (!this.properties.length) return true;
    if (ActionUpdateTags.includes(this.action.$type as ACTIONS_TYPES)) {
      return this.properties.some((prop) => {
        if (Array.isArray(prop.value)) {
          return prop.value.length === 0;
        }
      });
    }
    if (ActionUpdateField.includes(this.action.$type as ACTIONS_TYPES)) {
      return this.properties.some((prop) => {
        return (!prop.value || prop.value === 'none') && prop.name !== 'listId' && prop.name !== 'tagIds';
      });
    }
    return false;
  }

  get isDisabledBtnAddAction() {
    let disabledAction: boolean = false;
    if (Object.values(this.hasError).some((val) => val)) {
      disabledAction = true;
    } else {
      disabledAction = this.properties.some((prop) => {
        if (!prop.required) {
          return false;
        }
        if (Array.isArray(prop.value)) {
          return prop.value.length === 0 || prop.value.some((val) => val.trim() === '');
        } else if (prop.value !== '' && !isNaN(prop.value)) {
          return prop.value < 0;
        } else {
          return prop.value == null || prop.value == '' || prop.value == 'none';
        }
      });
    }
    return disabledAction;
  }

  get combinedPreferencesProperties() {
    let str = '';
    this.properties.forEach((prop) => {
      str +=
        this.translateService.instant('automations.actions.' + this.action.translationKey + '.' + prop.name) +
        '=' +
        this.translateService.instant('automations.actions.' + this.action.translationKey + '.' + prop.value) +
        '; ';
    });
    return str;
  }

  get isFieldMapping() {
    return this.nameGroup === TypeStep.FieldMapping;
  }

  get isActionRequiredList() {
    return ActionListField.includes(this.action.$type as ACTIONS_TYPES);
  }

  public get componentsAlwaysAdded() {
    const data = this.dataStateAndAccount;
    if (!data) return;
    let shouldBeAdded = [
      {
        subType:
          data.account.identifier === 'telephone'
            ? WidgetComponentSubType.InputTextPhone
            : WidgetComponentSubType.InputTextEmail,
        translationKey:
          data.account.identifier === 'telephone' ? 'settings.profile.phone-number' : 'settings.profile.email',
      },
      {
        subType: WidgetComponentSubType.ButtonListSubscriptionCheckbox,
        translationKey: 'campaign.widget.sidebar.edit.expansionPanelTitle.itemList-subscription',
      },
    ];
    if (
      data.actionsConfig.some((config) =>
        config.actionsGroup.some((item) => {
          return (
            item.action.$type === ACTIONS_TYPES.DotdigitalCreateContact ||
            item.action.$type === ACTIONS_TYPES.BrevoCreateContact ||
            (item.action.$type === ACTIONS_TYPES.VoyadoAddEmailToList &&
              item.action.properties.some((prop) => prop.name === 'isRunningInRealTime' && prop.value))
          );
        }),
      )
    ) {
      if (!shouldBeAdded.find((item) => item.subType === WidgetComponentSubType.InputTextPhone)) {
        shouldBeAdded = JSON.parse(
          JSON.stringify([
            ...shouldBeAdded,
            { subType: WidgetComponentSubType.InputTextPhone, translationKey: 'settings.profile.phone-number' },
            { subType: WidgetComponentSubType.InputTextRegular, translationKey: 'settings.profile.social-security' },
          ]),
        );
      }
    }
    const componentsAlwaysAdded = shouldBeAdded
      .map((comp) => {
        return (
          WidgetComponentHelpers.extractInputFieldComponentsFromState(data.widgetState).find(
            (input) =>
              input.subType === comp.subType &&
              ((input.subType === WidgetComponentSubType.InputTextRegular &&
                WidgetAutomationHelpers.getSocialSecurityInput(input)) ||
                input.subType !== WidgetComponentSubType.InputTextRegular),
          ) && comp
        );
      })
      .filter((comp) => comp);
    return componentsAlwaysAdded;
  }

  public checkRunningInRealTime(widgetState, account) {
    if (this.widgetAutomationsDataService.isActionCanBeWithRealTime(widgetState, this.action.nameAction, account)) {
      return true;
    }
  }

  get isBtnAddTagEnabled() {
    return !this.propValueTags?.length || this.propValueTags.every((item) => item.value);
  }

  get propList() {
    return [AutomationHelpers.pickPropListId(this.properties)];
  }

  get propsField() {
    const props = AutomationHelpers.excludePropListId(this.properties);
    return props;
  }

  get noItemsInFieldName() {
    if (this.propsField?.length) {
      const fieldName = this.propsField.find((item) => item.name === PROPS_TYPE.FieldName);
      if (fieldName) {
        return !fieldName.listUI?.length;
      }
    }
    return false;
  }

  get hasError() {
    //we can put here some other unique validations
    let isError: any = {};
    if (this.properties.length) {
      isError.allZero = this.properties.every((prop) => prop.typeInput === TypeInput.Number && prop.value === 0);
      // Validate any property marked as a URL or named 'url' (back-compat). Errors if any one is malformed.
      const urlProps = this.properties.filter((prop) => prop && (prop.isUrl === true || prop.name === 'url'));
      isError.url = urlProps.some((prop) => !this.isURL(prop?.value));
      isError.isoLangFormat = !this.isIsoFormat(this.properties.find((prop) => prop.name === 'language'));
    }
    return isError;
  }

  public isPropertyUrlInvalid(prop): boolean {
    return (prop?.isUrl === true || prop?.name === 'url') && !!prop?.value && !this.isURL(prop.value);
  }

  private isURL(url) {
    if (!url) return true;
    // Require explicit http:// or https:// scheme.
    return /^https?:\/\/\S+$/i.test(url);
  }

  private isIsoFormat(prop) {
    if (!prop || !prop.value || this.action.$type === ACTIONS_TYPES.VoyadoRegisterCart) return true;
    if (prop.value.length > 2 || prop.value.length === 1 || /[A-Z]/.test(prop.value)) return false;
    return true;
  }

  public toggleRealTimeVoyado(event, integrationType: IntegrationType) {
    // isBrevoDotVoyadoRuleRealTime reads this field, not the property ngModel writes, and it decides whether
    // the save forces the new-subscriber trigger on.
    if (this.action.properties?.some((prop) => prop.name === 'isRunningInRealTime')) {
      this.action.isRunningInRealTime = event?.checked === true;
    }
    this.dynamicModifyingMapFieldInRealTime.emit({ value: event, type: integrationType });
  }

  public trackByFn(index: number, item: any) {
    return index;
  }

  private checkOnMissingTagsOrLists(prop): void {
    if (ActionListField.includes(this.action.$type as ACTIONS_TYPES)) {
      const property = prop[0];
      if (
        property.value &&
        (property.name === PROPS_TYPE.TagIds ||
          property.name === PROPS_TYPE.ListId ||
          property.name === PROPS_TYPE.TagId)
      ) {
        const list = property.listUI?.map((item: any) => item.id) ?? [];
        if (Array.isArray(property.value)) {
          this.missingTagsOrLists = property.value.filter((v: string) => !list.includes(v));
        } else if (typeof property.value === 'string') {
          this.missingTagsOrLists = list.includes(property.value) ? [] : [property.value];
        }
      }
    }
  }

  public selectListIdsFromIntegration(prop): void {
    if (
      (prop.name === PROPS_TYPE.TagId || prop.name === PROPS_TYPE.TagIds || prop.name === PROPS_TYPE.ListId) &&
      this.listsIntegrations &&
      this.listsIntegrations[this.action.nameAction.toLowerCase()]
    ) {
      prop.listUI = prop.listUI.filter((obj) =>
        this.listsIntegrations[this.action.nameAction.toLowerCase()].includes(String(obj.id)),
      );
    }
  }

  private hasFieldMappingsProp(): boolean {
    return !!Object.values(this.action.fieldMappings ?? {}).length;
  }

  // Recompute the flag, which is shared by reference with the sibling "Add subscriber" step's
  // "Run in realtime" switch. No-op in the automation editor, where `actionsConfig` isn't provided.
  private recomputeFieldMappingFlag(): void {
    const actionsConfig = this.dataStateAndAccount?.actionsConfig;
    if (!actionsConfig) return;
    AutomationHelpers.setFieldsMappingActionProp([{ actionsConfig }]);
    // Keep each step's switch tooltip in sync with the recomputed flag.
    actionsConfig.forEach((config) =>
      config.actionsGroup?.forEach((group) => this.setRealTimeTooltipForAction(group.action)),
    );
    this.refreshRealTimeSwitchLock();
  }

  private updateSwitchRealTimeTooltip(): void {
    this.setRealTimeTooltipForAction(this.action);
  }

  // Computes the "Run in realtime" switch tooltip for a single action from its current field-mapping
  // state. Used both on load (for this.action) and when the shared flag is recomputed (for every
  // step's action), so the tooltip never goes stale relative to the switch's disabled state.
  private setRealTimeTooltipForAction(action): void {
    if (action?.nameAction !== IntegrationType.Voyado && action?.nameAction !== IntegrationType.Rule) {
      return;
    }
    const realTimeProp = action.properties?.find((p) => p.name === 'isRunningInRealTime');
    if (!realTimeProp) return;
    realTimeProp.tooltip = '';
    if (realTimeProp.value && !!Object.values(action.fieldMappings ?? {}).length) {
      realTimeProp.tooltip = 'automations.disable-turn-off-real-time-when-fieldMapping';
    }
    if (!realTimeProp.value && this.isRealTimeLockedFor(action)) {
      realTimeProp.tooltip = this.realTimeLockFromPersistedMappingOnly
        ? 'automations.disable-turn-on-real-time-when-saved-fieldMapping'
        : 'automations.disable-turn-on-real-time-when-fieldMappingAction';
    }
  }

  private isRealTimeLockedFor(action): boolean {
    if (!this.realTimeLockFromPersistedMappingOnly) {
      return action?.hasFieldsMappingAction === true;
    }
    return AutomationHelpers.hasPersistedFormMappedUpdateActions(
      this.widgetAutomationsDataService.existingAutomation,
      action?.nameAction,
    );
  }

  public onAddTag = (customField: string): { label: string; value: string } | null => {
    if (!customField?.trim()) return null;
    return this.inputFieldNames.has(customField) ? null : { label: customField, value: customField };
  };

  private get inputFieldNames(): Set<string> {
    const widgetStates = this.dataStateAndAccount?.widgetState ?? this.openingWidget?.states ?? [];
    const inputFields = WidgetComponentHelpers.extractInputFieldComponentsFromState(widgetStates);
    return new Set(inputFields.map((item) => `{{session.custom.${item.name}}}`));
  }

  // Not in the flyoutOpeningData setter: that can run before realTimeLockFromPersistedMappingOnly is bound.
  ngOnInit(): void {
    this.refreshRealTimeSwitchLock();
    this.updateSwitchRealTimeTooltip();
  }

  // Other steps' editors mutate hasFieldsMappingAction through the shared actionsConfig, so it is mirrored
  // every cycle rather than snapshotted. The persisted-mapping lock cannot change while the editor is open.
  ngDoCheck(): void {
    if (!this.realTimeLockFromPersistedMappingOnly) {
      this.realTimeSwitchLocked = this.action?.hasFieldsMappingAction === true;
    }
  }

  private refreshRealTimeSwitchLock(): void {
    this.realTimeSwitchLocked = this.isRealTimeLockedFor(this.action);
  }

  ngOnDestroy() {
    this.flyoutService.flyoutActionAddBS.next(null);
  }
}
